import AssignmentIcon from "@mui/icons-material/Assignment";
import FunctionsIcon from "@mui/icons-material/Functions";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ConstructionIcon from "@mui/icons-material/Construction";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import ArticleIcon from "@mui/icons-material/Article";
import {
  APP_MODULES,
  PLANT_MODULE_LICENCE_MAP,
  ALL_ESTIMATION_MODULE_NAMES,
} from "./appModules";

/**
 * Single source of truth for the per-project module/software tiles —
 * used by the quick-nav menu (PlantModuleNav) and the project hub page.
 * Keys match PLANT_MODULE_LICENCE_MAP / EPCM_SUB_MODULES accessor keys
 * in constants/appModules.js.
 */
//
// `description` is the one-line subtitle shown under the tile on the project
// hub - what the module is for, in the words the acronym stands for.
export const PLANT_MODULES = [
  {
    key: "task",
    label: "Action Items",
    description: "Tasks, owners and due dates",
    path: "task",
    icon: AssignmentIcon,
  },
  {
    key: "estimation",
    label: "Estimation",
    description: "Quantities and cost per WBS",
    path: "estimation",
    icon: FunctionsIcon,
  },
  {
    key: "dcs",
    label: "EMS",
    description: "Engineering documents and reviews",
    path: "dcs",
    icon: DocumentScannerIcon,
  },
  {
    key: "mcs",
    label: "PMS",
    description: "Procurement: equipment, valves, bulk materials",
    path: "mcs",
    icon: ManageAccountsIcon,
  },
  {
    key: "pcs",
    label: "CMS",
    description: "Construction: fabrication and ITP",
    path: "pcs",
    icon: ConstructionIcon,
  },
  {
    key: "validation",
    label: "Validation",
    description: "Compare revisions and drawings",
    path: "validation",
    icon: ChangeHistoryIcon,
  },
  {
    key: "cms",
    label: "COS",
    description: "Change orders and their impact",
    path: "cms",
    icon: ChangeCircleIcon,
  },
  {
    key: "pms",
    label: "PCS",
    description: "Planning, progress and cost control",
    path: "pms",
    icon: CalendarTodayIcon,
  },
  {
    key: "mis",
    label: "MIS",
    description: "Management reports and invoices",
    path: "mis",
    icon: DescriptionIcon,
  },
  {
    key: "docRegister",
    label: "Document Register",
    description: "Every project document and revision",
    path: "document-register",
    icon: ArticleIcon,
  },
];

// "dcs" (EMS) and "pcs" (CMS) are excluded here - they're broken out into
// their own groups below instead of one flat tile, same as "mcs" (PMS)
// already is via "PMS Registers".
const CORE_MODULE_KEYS = ["cms", "pms", "mis", "validation"];

/**
 * Grouped, richer catalogue of per-project destinations for the "apps"
 * launcher shown once a project is selected in the EMS side panel (see
 * EmsProjectsTreeView.jsx / PlantModuleNav's `groups` prop). Each item's
 * `module`/`modulesAny` gates visibility the same way PLANT_MODULES does;
 * `hash` (when present) is appended as `#key=value&...`, matching how each
 * module's own tabs deep-link into one another (MCS3/Main.jsx's `mcs-tab`,
 * BOMTab's `bom-tab`/`pnid-tab`, ECS's `ecs-tab` — see BOMTabs.js/ECSTabs.js
 * for the exact tab labels these hashes select).
 *
 * `wbsType` (Estimation items only) names the WBS discipline
 * ("struc"/"pip"/"pv") PlantModuleNav should list revisions for — from
 * `treeData.wbsByPlant`, which the sidebar tree has already loaded by the
 * time this menu is usable (no extra fetch for Structure/Piping; PV's
 * per-WBS input data is fetched lazily, on expand, through the tree's own
 * `loadPvInputsForWbs` cache so it's never fetched twice). `topTabLabel`/
 * `subTabLabel` feed straight into treeView/utils/estimationLinks.js's buildEstimationHref.
 *
 * Deliberately doesn't include every module named in the wider PLM roadmap
 * (Smart P&ID, 3D/2D automation, Digital Twin, CMMS, etc.) — those aren't
 * implemented as reachable pages yet, and a launcher tile should never point
 * at a route that doesn't exist.
 */
export const PLANT_MODULE_GROUPS = [
  {
    label: "Project",
    modules: [
      {
        key: "docRegister",
        label: "Document Register",
        path: "document-register",
        icon: ArticleIcon,
        module: APP_MODULES.EMS,
      },
      {
        key: "task",
        label: "Action Items",
        path: "task",
        icon: AssignmentIcon,
        module: APP_MODULES.TASK,
      },
    ],
  },
  {
    label: "Estimation",
    modules: [
      {
        key: "estimation-structure",
        label: "Estimation — Structural",
        path: "estimation",
        hash: { equipmentTab: "Structure" },
        icon: FunctionsIcon,
        module: APP_MODULES.ESTIMATION_STRUCTURE,
        wbsType: "struc",
        topTabLabel: "Structure",
      },
      {
        key: "estimation-piping",
        label: "Estimation — Piping",
        path: "estimation",
        hash: { equipmentTab: "Piping" },
        icon: FunctionsIcon,
        module: APP_MODULES.ESTIMATION_PIPING,
        wbsType: "pip",
        topTabLabel: "Piping",
      },
      {
        // No licence of its own yet - shown to anyone with any Estimation
        // licence, like the Instrumentation tab it opens (Tank_Estimation).
        key: "estimation-instrumentation",
        label: "Estimation — Instrumentation",
        path: "estimation",
        hash: { equipmentTab: "Instrumentation" },
        icon: FunctionsIcon,
        modulesAny: ALL_ESTIMATION_MODULE_NAMES,
        wbsType: "ins",
        topTabLabel: "Instrumentation",
      },
      {
        key: "estimation-pv",
        label: "Estimation — Pressure Vessel",
        path: "estimation",
        hash: { equipmentTab: "Pressure Vessel" },
        icon: FunctionsIcon,
        module: APP_MODULES.ESTIMATION_PV,
        wbsType: "pv",
        topTabLabel: "Pressure Vessel",
        subTabLabel: "Estimation",
      },
    ],
  },
  {
    // Sub-registers of the PMS (Procurement Management System) module —
    // same /plant/:plantId/mcs page as the "PMS" tile above, deep-linked
    // straight to one of its tabs.
    label: "PMS Registers",
    modules: [
      {
        key: "mcs-equipment-list",
        label: "Equipment List",
        path: "mcs",
        hash: { "mcs-tab": "Major Equipments", "ecs-tab": "Equipment List" },
        icon: ManageAccountsIcon,
        module: APP_MODULES.MCS,
      },
      {
        key: "mcs-valves-instrumentation",
        label: "Valve & Instrumentation List",
        path: "mcs",
        hash: {
          "mcs-tab": "Valves & Instrumentation",
          "pnid-tab": "Tag# Dashboard",
        },
        icon: ManageAccountsIcon,
        module: APP_MODULES.MCS,
      },
      {
        key: "mcs-bulk-materials",
        label: "Bulk Material List",
        path: "mcs",
        hash: { "mcs-tab": "Bulk Materials", "bom-tab": "BOM Dashboard" },
        icon: ManageAccountsIcon,
        module: APP_MODULES.MCS,
      },
    ],
  },
  {
    // Sub-tabs of the EMS (Engineering Management System) module - same
    // /plant/:plantId/dcs page as the flat "EMS" tile used to be, deep-linked
    // straight to one of its tabs (see DCSTab/index.jsx's `dcs-tab` hash).
    label: "EMS",
    modules: [
      {
        key: "dcs-doc-reviewer",
        label: "Doc. Reviewer",
        path: "dcs",
        hash: { "dcs-tab": "Doc. Reviewer" },
        icon: DocumentScannerIcon,
        module: APP_MODULES.EMS,
      },
    ],
  },
  {
    // Every module's own "4D Schedule" tab, pulled out of its module's group
    // and gathered here instead - each still opens that module's page
    // (mcs/dcs/pcs), just grouped by concern rather than split across menus.
    label: "4D Schedule",
    modules: [
      {
        key: "mcs-4d-schedule",
        label: "PMS 4D Schedule",
        path: "mcs",
        hash: { "mcs-tab": "4D Schedule" },
        icon: ManageAccountsIcon,
        module: APP_MODULES.MCS,
      },
      {
        key: "dcs-4d-schedule",
        label: "EMS 4D Schedule",
        path: "dcs",
        hash: { "dcs-tab": "4D Schedule" },
        icon: DocumentScannerIcon,
        module: APP_MODULES.EMS,
      },
      {
        key: "pcs-4d-schedule",
        label: "CMS 4D Schedule",
        path: "pcs",
        hash: { "pcs-tab": "4D Schedule" },
        icon: ConstructionIcon,
        module: APP_MODULES.CMS,
      },
    ],
  },
  {
    // Sub-tabs of the CMS (Construction Management System) module - same
    // /plant/:plantId/pcs page as the flat "CMS" tile used to be, deep-linked
    // straight to one of its tabs (see PCSTab/index.jsx's `pcs-tab` hash).
    label: "CMS",
    modules: [
      {
        key: "pcs-fabrication",
        label: "Fabrication",
        path: "pcs",
        hash: { "pcs-tab": "Fabrication" },
        icon: ConstructionIcon,
        module: APP_MODULES.CMS,
      },
      {
        key: "pcs-itp",
        label: "ITP",
        path: "pcs",
        // PCSTab/index.jsx's own tab is literally labeled "ITP " (trailing
        // space) - NavTabs matches hash values against that exact string, so
        // the space has to stay here even though the menu label above doesn't
        // show it.
        hash: { "pcs-tab": "ITP " },
        icon: ConstructionIcon,
        module: APP_MODULES.CMS,
      },
    ],
  },
  {
    label: "Modules",
    modules: PLANT_MODULES.filter((tile) =>
      CORE_MODULE_KEYS.includes(tile.key),
    ).map((tile) => ({
      ...tile,
      module: PLANT_MODULE_LICENCE_MAP[tile.key],
    })),
  },
];
